import type { AriaToastProps } from "@react-aria/toast";
import { useToast } from "@react-aria/toast";
import type { ToastState } from "@react-stately/toast";
import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import Button from "ds/components/Button";
import ToastNew from "ds/components/ToastNew";
import ToastActions from "ds/components/ToastNew/Actions";
import ToastDescription from "ds/components/ToastNew/Description";
import ToastTitle from "ds/components/ToastNew/Title";
import { useCssTransition } from "hooks/useCssTransition";
import { TransitionPhase } from "hooks/useCssTransition/types";
import { ignoreOutsideClick } from "hooks/useOutsideClick";
import { FlashEvent } from "types/FlashMessage";
import { useReducedMotion } from "views/Theme/useReducedMotion";

import styles from "./styles.module.css";
import { ToastTimer } from "./types";

/** Duration matches transition CSS in `styles.module.css` (enter). Exit uses View Transitions API via `wrapToastQueueUpdate`. */
const TRANSITION_MS = 200;

const isTestEnvironment = process.env.NODE_ENV === "test";
const testTransitionName = "test-transition-name";

type FlashMessageProps<T> = AriaToastProps<T> & {
  state: ToastState<T>;
};

const FlashMessage = (props: FlashMessageProps<FlashEvent>) => {
  const { state, toast, ...ariaProps } = props;
  const { title, message, variant, actionTitle, actionCallback } = toast.content;

  const action =
    (actionTitle &&
      actionCallback && {
        actionTitle,
        callback: actionCallback,
      }) ||
    undefined;

  const ref = useRef(null);
  const { toastProps, contentProps, titleProps, descriptionProps } = useToast(
    { ...ariaProps, toast },
    state,
    ref
  );

  // Timer pause/resume mutates @react-stately/toast's Timer without React state; re-read after effects run.
  const [, setTimerSync] = useState(0);
  useEffect(() => {
    setTimerSync((n) => n + 1);
  }, [toast.key]);

  const timer = toast.timer as ToastTimer | undefined;
  const isPaused = timer?.timerId === null;

  const dismiss = useCallback(() => {
    state.close(toast.key);
  }, [state, toast.key]);

  const { reducedMotion } = useReducedMotion();
  const durationMs = reducedMotion ? 0 : TRANSITION_MS;

  const { shouldRender, getClassNames } = useCssTransition({
    visible: true,
    timeout: durationMs,
    timeouts: {
      [TransitionPhase.APPEAR]: durationMs,
      [TransitionPhase.ENTER]: durationMs,
    },
    unmountOnExit: false,
    appear: true,
  });

  const timeoutMs = toast.timeout != null ? toast.timeout + 50 : undefined;

  const handleAction = () => {
    if (action?.callback) {
      action.callback();
    }

    dismiss();
  };

  if (!shouldRender) {
    return null;
  }

  const transitionClasses = getClassNames({
    enter: styles.enter,
    enterActive: styles.enterActive,
    exit: styles.exit,
    exitActive: styles.exitActive,
  });

  return (
    <div
      ref={ref}
      className={cx(styles.flashMessage, transitionClasses)}
      style={{ viewTransitionName: isTestEnvironment ? testTransitionName : toast.key }}
      {...toastProps}
      {...ignoreOutsideClick}
    >
      <div {...contentProps}>
        <ToastNew variant={variant} timeout={timeoutMs} isPaused={isPaused} onClose={dismiss}>
          <ToastTitle ariaProps={titleProps}>{title}</ToastTitle>
          <ToastDescription ariaProps={descriptionProps}>{message}</ToastDescription>
          {action && (
            <ToastActions>
              <Button onPress={handleAction} variant="contrast" size="small">
                {action.actionTitle}
              </Button>
            </ToastActions>
          )}
        </ToastNew>
      </div>
    </div>
  );
};

export default FlashMessage;
